/**
 * Alta de una línea de crédito.
 *
 * Puerto de `Credito::create` + `views/menu/credito/create.php`: el formulario del
 * panel anterior para registrar la línea de un cliente (cliente, tipo de crédito,
 * producto, monto total, precio, detalle de despacho, flexibilidad, vencimiento y
 * código externo de EDSuite), ya **sin depender de ningún recurso PHP**.
 *
 * Reglas conservadas:
 * - No puede haber **otra línea disponible** del mismo cliente y producto
 *   (`getCreditoDisponibleByClienteIdByproductoId`).
 * - La línea nace **disponible** (`credito_estado_id = 1`), activa, con su
 *   **movimiento inicial de flexibilidad** (`detalle = 'INICIAL'`) y el estado de
 *   pago en pendiente.
 * - El **detalle de despacho** es fijo (emisión de nota de venta) y los descuentos
 *   por producto se configuran después, con el lápiz del listado, como avisa el
 *   propio formulario del original.
 * - Con un producto elegido, la cantidad sale de `total / precio`; con «todos los
 *   productos» la línea es de importe (`precio = total`, `cantidad = 1`).
 */

import { UserRound, Wallet } from 'lucide-react';
import { useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { ApiError } from '@/lib/http/api-error';

import { useCatalogosCredito, useCrearCredito } from '../cierre.api';
import { useClientes } from '../clientes.api';
import { monto } from '../gestion-formato';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

interface Props {
  open: boolean;
  onClose: () => void;
  onAviso: (mensaje: string) => void;
}

export function CreditoNuevoDialog({ open, onClose, onAviso }: Props) {
  const catalogos = useCatalogosCredito();
  const crear = useCrearCredito();

  const [busqueda, setBusqueda] = useState('');
  const [clienteId, setClienteId] = useState(0);
  const [clienteNombre, setClienteNombre] = useState('');
  const [tipoCredito, setTipoCredito] = useState('CREDITO POR MONTOS TOTALES');
  const [productoId, setProductoId] = useState(0);
  const [total, setTotal] = useState('');
  const [precio, setPrecio] = useState('');
  const [flexibilidad, setFlexibilidad] = useState('');
  const [aplica, setAplica] = useState('NO');
  const [vencimiento, setVencimiento] = useState('');
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const clientes = useClientes({ search: busqueda, limit: 8 });
  const resultados = busqueda.trim().length >= 2 ? (clientes.data?.clientes ?? []) : [];

  const limpiar = () => {
    setClienteId(0);
    setClienteNombre('');
    setBusqueda('');
    setTotal('');
    setPrecio('');
    setFlexibilidad('');
    setVencimiento('');
    setCodigo('');
  };

  const guardar = () => {
    setError(null);

    if (!clienteId) {
      setError('Busca y selecciona el cliente de la línea.');

      return;
    }

    const importe = Number(total);

    if (!(importe > 0)) {
      setError('El monto de la línea debe ser mayor que cero.');

      return;
    }

    // Con un producto elegido, la cantidad sale del precio (como el original).
    const valorPrecio = productoId === 0 ? importe : Number(precio);

    if (productoId > 0 && !(valorPrecio > 0)) {
      setError('Indica el precio del producto para calcular la cantidad.');

      return;
    }

    crear.mutate(
      {
        cliente_id: clienteId,
        producto_id: productoId,
        tipo_credito: tipoCredito,
        total: importe,
        flexibilidad: Number(flexibilidad) || 0,
        aplicar_descuento: aplica,
        fecha_vencimiento: vencimiento || undefined,
        external_code_edsuite: codigo.trim() || undefined,
        precio: valorPrecio,
        cantidad: productoId === 0 ? 1 : importe / valorPrecio,
      },
      {
        onSuccess: (respuesta) => {
          limpiar();
          onClose();
          onAviso(respuesta.mensaje);
        },
        onError: (fallo) =>
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudo registrar el crédito.'),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title="Nueva línea de crédito"
      description="Registra la línea de un cliente; los descuentos por producto se configuran después con el lápiz del listado."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button size="sm" loading={crear.isPending} onClick={guardar}>
            Registrar crédito
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? (
          <Alert variant="error" title="No se pudo registrar">
            {error}
          </Alert>
        ) : null}

        <section className="rounded-card border border-ink-100 bg-white p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
            <UserRound className="size-4 text-brand-500" aria-hidden="true" />
            Cliente
          </h3>

          {clienteId ? (
            <div className="flex items-center justify-between gap-3 rounded-control border border-brand-200 bg-brand-50/50 px-3 py-2">
              <span className="text-sm font-medium text-ink-800">{clienteNombre}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setClienteId(0);
                  setClienteNombre('');
                  setBusqueda('');
                }}
              >
                Cambiar
              </Button>
            </div>
          ) : (
            <>
              <Input
                label="Buscar por nombre o documento"
                placeholder="Al menos 2 caracteres…"
                value={busqueda}
                onChange={(evento) => setBusqueda(evento.target.value)}
              />

              {clientes.isFetching ? <Skeleton className="mt-3 h-14 w-full" /> : null}

              {resultados.length ? (
                <ul className="mt-3 max-h-44 divide-y divide-ink-100 overflow-auto rounded-control border border-ink-100">
                  {resultados.map((cliente) => (
                    <li key={cliente.id}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-brand-50/60"
                        onClick={() => {
                          setClienteId(cliente.id);
                          setClienteNombre(cliente.nombre_comercial ?? cliente.nombre ?? '');
                        }}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-ink-800">
                            {cliente.nombre_comercial ?? cliente.nombre}
                          </span>
                          <span className="block text-xs text-ink-500">{cliente.numero_doc}</span>
                        </span>
                        {cliente.withholding ? (
                          <span className="text-xs text-ink-500">
                            Retención {cliente.withholding_percentage}%
                          </span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          )}
        </section>

        <section className="rounded-card border border-ink-100 bg-white p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
            <Wallet className="size-4 text-brand-500" aria-hidden="true" />
            Línea
          </h3>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Tipo de crédito</span>
              <Select
                className={CONTROL}
                value={tipoCredito}
                onChange={(evento) => setTipoCredito(evento.target.value)}
              >
                {(catalogos.data?.tipos_credito ?? [tipoCredito]).map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {tipo}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Producto</span>
              <Select
                className={CONTROL}
                value={productoId}
                onChange={(evento) => setProductoId(Number(evento.target.value))}
              >
                <option value={0}>TODOS LOS PRODUCTOS</option>
                {(catalogos.data?.productos ?? []).map((producto) => (
                  <option key={producto.id} value={producto.id}>
                    {producto.nombre}
                  </option>
                ))}
              </Select>
            </label>

            <Input
              label="Monto total de la línea (S/)"
              type="number"
              min={0}
              step="0.01"
              value={total}
              onChange={(evento) => setTotal(evento.target.value)}
            />

            <Input
              label="Precio (S/)"
              type="number"
              min={0}
              step="0.01"
              disabled={productoId === 0}
              hint={productoId === 0 ? 'Con todos los productos la línea es de importe' : undefined}
              value={productoId === 0 ? total : precio}
              onChange={(evento) => setPrecio(evento.target.value)}
            />

            <Input
              label="Flexibilidad (S/)"
              type="number"
              min={0}
              step="0.01"
              value={flexibilidad}
              onChange={(evento) => setFlexibilidad(evento.target.value)}
            />

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">¿Aplica descuento?</span>
              <Select
                className={CONTROL}
                value={aplica}
                onChange={(evento) => setAplica(evento.target.value)}
              >
                <option value="NO">No aplicar descuento</option>
                <option value="SI">Sí, aplicar descuento en despachos</option>
              </Select>
            </label>

            <Input
              label="Vencimiento (opcional)"
              type="date"
              value={vencimiento}
              onChange={(evento) => setVencimiento(evento.target.value)}
            />

            <Input
              label="Código externo de EDSuite"
              placeholder="Opcional, para centralización"
              value={codigo}
              onChange={(evento) => setCodigo(evento.target.value)}
            />
          </div>

          {productoId > 0 && Number(total) > 0 && Number(precio) > 0 ? (
            <p className="mt-3 text-xs text-ink-500">
              Saldrían {(Number(total) / Number(precio)).toFixed(2)} unidades a S/{' '}
              {monto(Number(precio))}.
            </p>
          ) : null}
        </section>

        <Alert variant="info" title="Qué pasa al registrar">
          <span className={ICONO_EN_ALERTA}>
            La línea nace <b>disponible</b> (estado «Disponible») con su movimiento inicial de
            flexibilidad, y no se admite otra línea disponible del mismo cliente y producto. Los
            descuentos por producto se configuran después con el lápiz del listado.
          </span>
        </Alert>
      </div>
    </Dialog>
  );
}
